import { WEATHER_SOURCE_CODES } from "./weatherSourceCodes.js";
import {
  AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
  AEROCIVIL_AVIATION_RADAR_TRUST_LABEL,
} from "./aerocivilAviationRadar.js";

export const COLOMBIA_IDEAM_LIVE_SOURCE_KEY = "IDEAM_RADAR_COMPOSITE";
export const COLOMBIA_RAINVIEWER_FALLBACK_SOURCE_KEY = "RAINVIEWER_COLOMBIA";

export const COLOMBIA_WEATHER_SOURCE_CATEGORY_LABELS = {
  live_overlay: "Live overlay",
  local_replay: "Historical replay",
  fallback: "Fallback",
};

/** @type {Record<string, object>} */
export const COLOMBIA_WEATHER_SOURCE_METADATA = {
  IDEAM_RADAR_COMPOSITE: {
    sourceKey: "IDEAM_RADAR_COMPOSITE",
    displayName: "Colombia Live Radar",
    shortName: "IDEAM Live",
    category: "live_overlay",
    sourceType: "arcgis_mapserver",
    operationalRole: "primary_live_radar",
    description:
      "Official IDEAM Colombia live radar composite overlay served from an ArcGIS MapServer endpoint.",
    adminDescription:
      "Live Colombia radar source. Uses the official IDEAM ArcGIS MapServer overlay.",
    supportsLiveRadar: true,
    supportsReplay: false,
    requiresLocalFiles: false,
    isFallback: false,
    isPrimaryLiveColombiaRadar: true,
    layerControlLabel: "Colombia Live Radar",
  },
  IDEAM_RADAR: {
    sourceKey: "IDEAM_RADAR",
    displayName: "IDEAM Historical Radar Replay",
    shortName: "IDEAM Replay",
    category: "local_replay",
    sourceType: "local_tile_pipeline",
    operationalRole: "historical_radar_replay",
    description:
      "Local/historical IDEAM radar pipeline using staged RAW radar files, processed frames, and generated PNG tiles.",
    adminDescription:
      "Historical/local radar replay source. Requires staged RAW radar files and generated tiles. This is not the live Colombia radar source.",
    supportsLiveRadar: false,
    supportsReplay: true,
    requiresLocalFiles: true,
    isFallback: false,
    isPrimaryLiveColombiaRadar: false,
  },
  RAINVIEWER_COLOMBIA: {
    sourceKey: "RAINVIEWER_COLOMBIA",
    displayName: "RainViewer Colombia Fallback",
    shortName: "RainViewer",
    category: "fallback",
    sourceType: "external_radar",
    operationalRole: "fallback_live_radar",
    description:
      "External radar fallback for Colombia when IDEAM live radar is unavailable.",
    adminDescription:
      "Fallback radar source used when IDEAM live radar is unavailable.",
    supportsLiveRadar: true,
    supportsReplay: false,
    requiresLocalFiles: false,
    isFallback: true,
    isPrimaryLiveColombiaRadar: false,
  },
  AEROCIVIL_AVIATION_RADAR: {
    sourceKey: "AEROCIVIL_AVIATION_RADAR",
    displayName: "Aerocivil Aviation Radar",
    shortName: "Aerocivil",
    category: "live_overlay",
    sourceType: "aviation_radar",
    operationalRole: "complementary_aeronautical_radar",
    description:
      "Official aeronautical radar from Aerocivil LEADS WxWeb portal. Complementary layer — not Valley Preferred.",
    adminDescription:
      "Aerocivil aeronautical radar — discovery, cache sync, and health checks when AEROCIVIL_RADAR_ENABLED=true.",
    supportsLiveRadar: true,
    supportsReplay: false,
    requiresLocalFiles: false,
    isFallback: false,
    isPrimaryLiveColombiaRadar: false,
    trustLabel: AEROCIVIL_AVIATION_RADAR_TRUST_LABEL,
    attribution: AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
    layerControlLabel: "Aerocivil aviation radar",
  },
};

export function getWeatherSourceMetadata(sourceKey) {
  if (!sourceKey) {
    return null;
  }

  return COLOMBIA_WEATHER_SOURCE_METADATA[String(sourceKey).toUpperCase()] || null;
}

export function getWeatherSourceDisplayName(sourceKey, fallback = null) {
  return getWeatherSourceMetadata(sourceKey)?.displayName || fallback || sourceKey;
}

export function getWeatherSourceCategoryLabel(sourceKey) {
  const metadata = getWeatherSourceMetadata(sourceKey);
  if (!metadata?.category) {
    return null;
  }

  return COLOMBIA_WEATHER_SOURCE_CATEGORY_LABELS[metadata.category] || metadata.category;
}

export function isLiveRadarSource(sourceKey) {
  return Boolean(getWeatherSourceMetadata(sourceKey)?.supportsLiveRadar);
}

export function isHistoricalReplaySource(sourceKey) {
  return Boolean(getWeatherSourceMetadata(sourceKey)?.supportsReplay);
}

export function isFallbackRadarSource(sourceKey) {
  return Boolean(getWeatherSourceMetadata(sourceKey)?.isFallback);
}

export function isPrimaryLiveColombiaRadarSource(sourceKey) {
  return Boolean(getWeatherSourceMetadata(sourceKey)?.isPrimaryLiveColombiaRadar);
}

export function getColombiaLiveRadarLayerLabel() {
  return (
    getWeatherSourceMetadata(COLOMBIA_IDEAM_LIVE_SOURCE_KEY)?.layerControlLabel ||
    "Colombia Live Radar"
  );
}

export const IDEAM_HISTORICAL_REPLAY_SOURCE_CODE = WEATHER_SOURCE_CODES.IDEAM_RADAR;
